import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody, CardHeader, Badge, Table, Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FaMoneyBillWave, FaClock, FaCheckCircle, FaEye } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice } from '../../helpers/utility';
import { REGLEMENTS_PAGE } from '../../routes/routeConstants';

const ReglementWidget = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list } = useSelector(state => state.reglements);
  const { clients } = useSelector(state => state.clients);

  useEffect(() => {
    dispatch(reglementActions.listRequest({ limit: 10 }));
  }, [dispatch]);

  // Règlements récents (derniers 5)
  const reglementsRecents = list
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  // Règlements en attente
  const reglementsEnAttente = list.filter(r => r.statut === 'EN_ATTENTE');

  // Statistiques rapides
  const totalMontantMois = list
    .filter(r => {
      const dateReglement = new Date(r.dateReglement);
      const maintenant = new Date();
      return dateReglement.getMonth() === maintenant.getMonth() && 
             dateReglement.getFullYear() === maintenant.getFullYear();
    })
    .reduce((sum, r) => sum + (r.montantRegle || 0), 0);

  const getStatutBadge = (statut) => {
    const badges = {
      'EN_ATTENTE': 'warning',
      'VALIDE': 'info',
      'ENCAISSE': 'success',
      'REJETE': 'danger',
      'ANNULE': 'secondary'
    };
    
    return (
      <Badge color={badges[statut] || 'secondary'} size="sm">
        {t(`reglement.${statut.toLowerCase()}`)}
      </Badge>
    );
  };

  return (
    <div className="row">
      {/* Statistiques rapides */}
      <div className="col-md-4">
        <Card className="shadow-sm h-100">
          <CardHeader className="bg-primary text-white">
            <h6 className="mb-0">
              <FaMoneyBillWave className="me-2" />
              {t('reglement.statistiques')}
            </h6>
          </CardHeader>
          <CardBody>
            <div className="mb-3">
              <div className="text-muted small">{t('reglement.totalMoisCourant')}</div>
              <div className="h4 text-primary mb-0">
                {formatPrice(totalMontantMois)}
              </div>
            </div>
            <div className="mb-3">
              <div className="text-muted small">{t('reglement.enAttente')}</div>
              <div className="h5 text-warning mb-0">
                {reglementsEnAttente.length}
              </div>
            </div>
            <Button 
              color="primary" 
              size="sm" 
              onClick={() => navigate(REGLEMENTS_PAGE)}
              block
            >
              <FaEye className="me-1" />
              {t('reglement.voirTous')}
            </Button>
          </CardBody>
        </Card>
      </div>

      {/* Règlements en attente */}
      <div className="col-md-4">
        <Card className="shadow-sm h-100">
                    <CardHeader className="bg-warning text-white">
            <h6 className="mb-0">
              <FaClock className="me-2" />
              {t('reglement.enAttente')} ({reglementsEnAttente.length})
            </h6>
          </CardHeader>
          <CardBody className="p-0">
            {reglementsEnAttente.length > 0 ? (
              <Table size="sm" className="mb-0">
                <tbody>
                  {reglementsEnAttente.slice(0, 5).map((reglement) => {
                    const client = clients.find(c => c.id === reglement.clientId);
                    return (
                      <tr key={reglement.id}>
                        <td className="py-2">
                          <div className="fw-bold small">
                            {reglement.reference || reglement.id}
                          </div>
                          <div className="text-muted small">
                            {client ? `${client.nom} ${client.prenom || ''}`.trim() : 'N/A'}
                          </div>
                        </td>
                        <td className="text-end py-2">
                          <div className="fw-bold small text-warning">
                            {formatPrice(reglement.montantRegle, false)}
                          </div>
                          <div className="text-muted small">
                            {new Date(reglement.dateReglement).toLocaleDateString('fr-FR')}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="p-3 text-center text-muted">
                <FaCheckCircle className="mb-2" size={24} />
                <div>{t('reglement.aucunEnAttente')}</div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Règlements récents */}
      <div className="col-md-4">
        <Card className="shadow-sm h-100">
          <CardHeader className="bg-success text-white">
            <h6 className="mb-0">
              <FaCheckCircle className="me-2" />
              {t('reglement.recents')}
            </h6>
          </CardHeader>
          <CardBody className="p-0">
            {reglementsRecents.length > 0 ? (
              <Table size="sm" className="mb-0">
                <tbody>
                  {reglementsRecents.map((reglement) => {
                    const client = clients.find(c => c.id === reglement.clientId);
                    return (
                      <tr key={reglement.id}>
                        <td className="py-2">
                          <div className="fw-bold small">
                            {reglement.reference || reglement.id}
                          </div>
                          <div className="text-muted small">
                            {client ? `${client.nom} ${client.prenom || ''}`.trim() : 'N/A'}
                          </div>
                        </td>
                        <td className="text-end py-2">
                          <div className="fw-bold small">
                            {formatPrice(reglement.montantRegle, false)}
                          </div>
                          <div>
                            {getStatutBadge(reglement.statut)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <div className="p-3 text-center text-muted">
                <div>{t('reglement.aucunRecent')}</div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default ReglementWidget;

